/**
 * PortfolioHistoricoPage — "¿Como se ha comportado?"
 * Row 1: Portfolio evolution (balance + alive credits)
 * Row 2: Delinquency evolution (rate + amount)
 * Row 3: Resolution (paid vs default + recovery rate)
 * Selector: Mensual / Anual
 */

import { useState } from 'react';
import {
  RefreshCw,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { formatCurrency } from '@/utils/formatters';
import { ROUTES, PARTNER_ID } from '@/constants';

import { usePortfolioV2 } from '@/features/portfolio/hooks/usePortfolioV2';
import { useQuery } from '@tanstack/react-query';
import { getPortfolioResolution } from '@/services/credit-engine.service';

interface BalanceMonthlyItem { month: string; alive_credits: number; total_outstanding_mxn: number }
interface ResolutionItem { month: string; paid_off: number; defaulted: number; cancelled: number; total_resolved: number; recovery_rate_pct: number }

// ─── Constants ──────────────────────────────────────────────────────────────

const BREADCRUMBS = [
  { label: 'Inicio', path: ROUTES.DASHBOARD },
  { label: 'Portafolio', path: ROUTES.PORTFOLIO_DASHBOARD },
  { label: 'Historico', path: '#' },
];

const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Mensual' },
  { value: 'annual', label: 'Anual' },
];

function formatMonthLabel(month: string): string {
  const parts = month.split('-');
  const year = parts[0] ?? '';
  const m = parts[1] ?? '01';
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  return `${months[parseInt(m, 10) - 1]} ${year.slice(2)}`;
}

// ─── Balance Chart ──────────────────────────────────────────────────────────

function BalanceChart({ data }: { data: BalanceMonthlyItem[] }) {
  const chartData = data.map((d) => ({ ...d, label: formatMonthLabel(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `$${(v / 1_000_000).toFixed(1)}M`} width={50} />
        <Tooltip formatter={(value) => [formatCurrency(Number(value), 'MXN', 'es-MX'), 'Saldo Vivo']} labelFormatter={(label) => String(label)} />
        <Bar dataKey="total_outstanding_mxn" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={28} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Alive Credits Chart ────────────────────────────────────────────────────

function AliveCreditChart({ data }: { data: BalanceMonthlyItem[] }) {
  const chartData = data.map((d) => ({ ...d, label: formatMonthLabel(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={40} />
        <Tooltip formatter={(value) => [value, 'Creditos Vivos']} labelFormatter={(label) => String(label)} />
        <Line type="monotone" dataKey="alive_credits" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4, fill: '#3b82f6' }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Delinquency History Chart ──────────────────────────────────────────────

interface DelinquencyHistoryItem { month: string; delinquency_rate_pct: number; }

function DelinquencyRateChart({ data }: { data: DelinquencyHistoryItem[] }) {
  const chartData = data.map((d) => ({ ...d, label: formatMonthLabel(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `${v}%`} domain={[0, 'auto']} width={40} />
        <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Tasa de Mora']} labelFormatter={(label) => String(label)} />
        <Line type="monotone" dataKey="delinquency_rate_pct" stroke="#ef4444" strokeWidth={2} dot={{ r: 4, fill: '#ef4444' }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Resolution Chart ───────────────────────────────────────────────────────

function ResolutionChart({ data }: { data: ResolutionItem[] }) {
  const chartData = data.map((d) => ({ ...d, label: formatMonthLabel(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={30} />
        <Tooltip labelFormatter={(label) => String(label)} />
        <Legend />
        <Bar dataKey="paid_off" name="Pagados" fill="#22c55e" radius={[4, 4, 0, 0]} stackId="a" isAnimationActive={false} />
        <Bar dataKey="defaulted" name="Default" fill="#ef4444" radius={[0, 0, 0, 0]} stackId="a" isAnimationActive={false} />
        <Bar dataKey="cancelled" name="Cancelados" fill="#9ca3af" radius={[0, 0, 4, 4]} stackId="a" isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Recovery Rate Chart ────────────────────────────────────────────────────

function RecoveryRateChart({ data }: { data: ResolutionItem[] }) {
  const chartData = data.map((d) => ({ ...d, label: formatMonthLabel(d.month) }));
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={chartData} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} tickFormatter={(v: number) => `${v}%`} domain={[0, 100]} width={40} />
        <Tooltip formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Tasa Recuperacion']} labelFormatter={(label) => String(label)} />
        <Line type="monotone" dataKey="recovery_rate_pct" stroke="#22c55e" strokeWidth={2} dot={{ r: 4, fill: '#22c55e' }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export function PortfolioHistoricoPage() {
  const [period, setPeriod] = useState('monthly');
  const portfolio = usePortfolioV2();
  const { data: resolutionData } = useQuery({ queryKey: ['portfolio-resolution'], queryFn: () => getPortfolioResolution(PARTNER_ID) });
  const resolution: ResolutionItem[] = resolutionData?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Portafolio Historico" helpKey="portfolio-historico"
        subtitle="¿Como se ha comportado el portafolio?"
        breadcrumbs={BREADCRUMBS}
        actions={
          <div className="flex items-center gap-2">
            
            <Button onClick={portfolio.refetch}>
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        <div className="flex justify-end">
          <Select
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
            placeholder="Periodo"
          />
        </div>

        {/* ── Row 1: Portfolio Evolution ───────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="Saldo Vivo" description="Evolucion del saldo pendiente">
            <BalanceChart data={portfolio.balanceMonthly} />
          </Card>
          <Card title="Creditos Vivos" description="Cantidad de creditos activos por periodo">
            <AliveCreditChart data={portfolio.balanceMonthly} />
          </Card>
        </div>

        {/* ── Row 2: Delinquency Evolution ─────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="Tasa de Morosidad" description="Evolucion mensual">
            <DelinquencyRateChart data={portfolio.delinquencyHistory} />
          </Card>
          <Card title="Monto en Mora" description="Evolucion del monto moroso">
            <BalanceChart data={portfolio.balanceMonthly} />
          </Card>
        </div>

        {/* ── Row 3: Resolution ────────────────────────────────────── */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="Resolucion de Creditos" description="Pagados vs Default vs Cancelados por periodo">
            <ResolutionChart data={resolution} />
          </Card>
          <Card title="Tasa de Recuperacion" description="% de creditos resueltos exitosamente">
            <RecoveryRateChart data={resolution} />
          </Card>
        </div>
      </div>
    </div>
  );
}
